import { Table, Tag, UnstyledButton, VisuallyHidden } from '@zillow/constellation-10';
import { ArrowDown } from 'lucide-react';
import { useLocale, useTranslations } from 'next-intl';
import type { PropertyDetailListing } from '@/src/components/features/property/types';
import { LucideIcon } from '@/src/components/LucideIcon';
import type { FloorplanMedia } from './floorplanMedia';
import { formatUnitPrice } from './unitPricing';
import * as styles from './unitsModule.styles';

type Unit = FloorplanMedia & {
  modelId: string;
  name: string;
  availabilityDate: string;
  beds: number;
  baths: number;
  sqft: number;
  low: number;
  high: number;
  priceRangeDisplay: Array<string>;
  isNewUnit?: boolean;
  createdAgo?: string;
  priceDrop?: number;
};

type Floorplan = FloorplanMedia & {
  modelId: string;
  name: string;
  beds: number;
  baths: number;
  sqft: number;
  low: number;
  high: number;
  priceRangeDisplay: Array<string>;
  zillow3dTourUrl?: string;
  units: Array<Unit>;
};

type UnitTableProps = {
  units: Array<Unit>;
  floorplan: Floorplan;
  listing?: PropertyDetailListing;
  onUnitSelect: (unit: Unit, floorplan: Floorplan) => void;
  maxRows?: number;
  onSeeAll?: () => void;
};

const formatAvailability = (date: string, nowLabel: string, dateLocale: string): string => {
  if (!date) return nowLabel;
  const lower = date.toLowerCase();
  if (lower === 'now' || lower === 'available now' || lower === 'available') return nowLabel;

  const parsed = new Date(date);
  if (!isNaN(parsed.getTime())) {
    return parsed.toLocaleDateString(dateLocale, { timeZone: 'UTC' });
  }

  return date;
};

export const UnitTable = ({ units, floorplan, listing, onUnitSelect, maxRows, onSeeAll }: UnitTableProps) => {
  const t = useTranslations('hdp');
  const locale = useLocale();
  const numberLocale = locale === 'es' ? 'es' : 'en-US';
  const callLabel = t('callForPrice');
  const nowLabel = t('now');
  const hasUnits = units && units.length > 0;
  const displayedUnits = maxRows && hasUnits ? units.slice(0, maxRows) : units;
  const hasMore = maxRows && hasUnits && units.length > maxRows;
  const totalCount = hasUnits ? units.length : 0;
  const showsTotalMonthlyPrice = listing?.hasTotalMonthlyPrice === true;

  return (
    <>
      <Table.Responsive className={styles.unitTableResponsive}>
        <Table.Root
          aria-label={t('unitTableAria', { name: floorplan.name })}
          appearance="bare"
          size="sm"
          className={styles.unitTable}
        >
          <colgroup>
            <col className={styles.unitNameColumn} />
            <col className={styles.unitRentColumn} />
            <col />
            <col className={styles.unitDetailsColumn} />
          </colgroup>
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell scope="col" className={styles.unitTableHeader}>
                {t('unitName')}
              </Table.HeaderCell>
              <Table.HeaderCell scope="col" className={styles.unitTableHeader}>
                {showsTotalMonthlyPrice ? t('totalPricePerMo') : t('rentPerMo')}
              </Table.HeaderCell>
              <Table.HeaderCell scope="col" align="right" className={styles.unitTableHeaderAvailable}>
                {t('available')}
              </Table.HeaderCell>
              <Table.HeaderCell scope="col" className={styles.unitTableHeaderChevron}>
                <VisuallyHidden>{t('unitDetailsColumn')}</VisuallyHidden>
              </Table.HeaderCell>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {hasUnits ? (
              displayedUnits.map((unit) => {
                const rentLabel = formatUnitPrice(unit.low, callLabel, numberLocale);
                const availabilityLabel = formatAvailability(unit.availabilityDate, nowLabel, numberLocale);
                const selectUnitAriaLabel = showsTotalMonthlyPrice
                  ? t('selectUnitTotalPriceAria', {
                      name: unit.name,
                      rent: rentLabel,
                      availability: availabilityLabel,
                    })
                  : t('selectUnitAria', {
                      name: unit.name,
                      rent: rentLabel,
                      availability: availabilityLabel,
                    });

                return (
                  <Table.Row
                    key={unit.modelId || unit.name}
                    className={styles.unitTableRow}
                    interactive
                    onClick={() => onUnitSelect(unit, floorplan)}
                  >
                    <Table.Cell className={styles.unitNameCell}>
                      <UnstyledButton
                        type="button"
                        className={styles.unitNameButton}
                        onClick={(event) => {
                          event.stopPropagation();
                          onUnitSelect(unit, floorplan);
                        }}
                        aria-label={selectUnitAriaLabel}
                      >
                        <span className={styles.badgeArea}>
                          {unit.isNewUnit && !unit.priceDrop && (
                            <Tag tone="teal" size="sm">
                              {t('newBadge')}
                            </Tag>
                          )}
                        </span>
                        <span className={styles.unitNameText} data-unit-name-text>
                          {unit.name}
                        </span>
                      </UnstyledButton>
                    </Table.Cell>
                    <Table.Cell className={styles.cellWithBadgeArea}>
                      <span className={styles.rentCellContent}>
                        <span className={styles.badgeArea}>
                          {unit.priceDrop ? (
                            <span className={styles.priceDropOriginal}>
                              {formatUnitPrice(unit.low + unit.priceDrop, callLabel, numberLocale)}
                            </span>
                          ) : null}
                        </span>
                        <span className={styles.rentValue}>
                          <span className={styles.rentText}>{rentLabel}</span>
                          {unit.priceDrop ? (
                            <LucideIcon
                              className={styles.priceDropArrow}
                              icon={ArrowDown}
                              title={t('priceDropAria', {
                                amount: `$${unit.priceDrop.toLocaleString(numberLocale)}`,
                              })}
                            />
                          ) : null}
                        </span>
                      </span>
                    </Table.Cell>
                    <Table.Cell align="right" className={styles.availableCell}>
                      {availabilityLabel}
                    </Table.Cell>
                    <Table.Cell className={styles.chevronCell} aria-hidden="true">
                      <span className={styles.chevronChar}>{'\u203A'}</span>
                    </Table.Cell>
                  </Table.Row>
                );
              })
            ) : (
              <Table.Row>
                <Table.Cell className={styles.unitNameCell}>
                  <span className={styles.placeholderValue}>{callLabel}</span>
                </Table.Cell>
                <Table.Cell className={styles.cellWithBadgeArea}>
                  <span className={styles.placeholderValue}>{callLabel}</span>
                </Table.Cell>
                <Table.Cell align="right" className={styles.availableCell}>
                  {callLabel}
                </Table.Cell>
                <Table.Cell className={styles.chevronCell} aria-hidden="true">
                  <span />
                </Table.Cell>
              </Table.Row>
            )}
          </Table.Body>
        </Table.Root>
      </Table.Responsive>

      {hasMore && onSeeAll && (
        <button type="button" className={styles.seeAllLink} onClick={onSeeAll}>
          {t('seeAllUnits', { count: totalCount })} <span className={styles.seeAllArrow}>{'\u2192'}</span>
        </button>
      )}
    </>
  );
};

export type { Unit, Floorplan };
